import {
	getCashflowReportExportById,
	getInvoiceExportById,
	getPayoutReportExportById,
	getUnpaidCompletedWorkReportExportById,
	markCashflowReportExportDownloaded,
	markInvoiceExportDownloaded,
	markPayoutReportExportDownloaded,
	markUnpaidCompletedWorkReportExportDownloaded,
	type ReportExportDto,
	retryCashflowReportExport,
	retryInvoiceExport,
	retryPayoutReportExport,
	retryUnpaidCompletedWorkReportExport,
} from "@fp/admin-api-client";
import type { AxiosResponse } from "axios";

/**
 * Every report export is read back, retried and marked downloaded the same way, through its own
 * endpoints so each carries the permission that gates the report. Those endpoints are what differ
 * between reports. They are what the shared hook, toast and download page take.
 */
export type ReportExportClient = {
	getById: (id: string) => Promise<AxiosResponse<ReportExportDto>>;
	retry: (id: string) => Promise<AxiosResponse<ReportExportDto>>;
	markDownloaded: (id: string) => Promise<AxiosResponse<void>>;
	noun: string;
	downloadPage: string;
	origin: { url: string; label: string };
};

export const invoiceExportClient: ReportExportClient = {
	getById: getInvoiceExportById,
	retry: retryInvoiceExport,
	markDownloaded: markInvoiceExportDownloaded,
	noun: "invoice",
	downloadPage: "/invoice-downloads",
	origin: { url: "/payout-requests", label: "Payout requests" },
};

export const payoutReportExportClient: ReportExportClient = {
	getById: getPayoutReportExportById,
	retry: retryPayoutReportExport,
	markDownloaded: markPayoutReportExportDownloaded,
	noun: "payout report",
	downloadPage: "/payout-report-downloads",
	origin: { url: "/reporting/payouts", label: "Payout Report" },
};

export const cashflowReportExportClient: ReportExportClient = {
	getById: getCashflowReportExportById,
	retry: retryCashflowReportExport,
	markDownloaded: markCashflowReportExportDownloaded,
	noun: "cashflow report",
	downloadPage: "/cashflow-report-downloads",
	origin: { url: "/reporting/cashflow", label: "Cashflow Report" },
};

export const unpaidCompletedWorkReportExportClient: ReportExportClient = {
	getById: getUnpaidCompletedWorkReportExportById,
	retry: retryUnpaidCompletedWorkReportExport,
	markDownloaded: markUnpaidCompletedWorkReportExportDownloaded,
	noun: "unpaid completed work report",
	downloadPage: "/unpaid-completed-work-report-downloads",
	origin: { url: "/reporting/unpaid-completed-work", label: "Unpaid Completed Work" },
};
